// api
import { api } from '@/services'

// libs
import qs from 'qs'

// vue core
import Vue from 'vue'

// utils
import { createCancelPreviousRequest, isCancelError } from '@/util/cancelPreviousRequest'

// helpers
const getRoles = () => (localStorage.getItem('access_roles') || '').split(',').filter(Boolean)

const isAdminOrPartner = () => {
  const roles = getRoles()
  return roles.includes('ROLE_ADMIN') || roles.includes('ROLE_PARTNER')
}

// constants
const adminEndpoint = 'admin'
const customerEndpoint = 'web'

const endpoint = () => (isAdminOrPartner() ? adminEndpoint : customerEndpoint)

// request control
const fetchAppointmentsAsAdminLatestRequest = createCancelPreviousRequest()

// vuex module
const state = {
  isAdminAppointmentTypeLoading: false,
  isAppointmentRescheduling: false,
}

const getters = {
  isAdminAppointmentTypeLoading: (state) => state.isAdminAppointmentTypeLoading,
  isAppointmentRescheduling: (state) => state.isAppointmentRescheduling,
}

const actions = {
  async fetchAppointmentAsAdmin({ commit, dispatch }, id) {
    if (!id) return

    commit('setIsLoading', true)
    commit('setAppointment', {})

    try {
      const { data } = await api.get(`/${endpoint()}/appointment/details/${id}`)
      commit('setAppointment', data)
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async fetchAppointmentsAsAdmin({ commit, dispatch, rootState }) {
    const { cancelToken, isLatest } = fetchAppointmentsAsAdminLatestRequest.start(
      'Canceled because a newer fetchAppointmentsAsAdmin started'
    )

    commit('setIsLoading', true)

    const defaultParams = {
      size: rootState.paginate.itemsPerPage,
      page: rootState.paginate.currentPage - 1,
    }
    const params = { ...defaultParams, ...rootState.filters.filters }

    try {
      const { data } = await api.get('/admin/appointment/list', {
        cancelToken,
        params,
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      })

      if (!isLatest()) return

      const { result, total } = data

      commit('setAppointments', result)
      commit('setTotalPages', total)
    } catch (error) {
      if (isCancelError(error)) return
      if (!isLatest()) return

      if (typeof error === 'string') {
        dispatch('showErrorMessage', error)
        return
      }

      const data = error?.data ?? error?.response?.data
      dispatch('showErrorMessage', data?.message)
    } finally {
      if (isLatest()) {
        commit('setIsLoading', false)
      }
    }
  },

  async fetchAppointmentTypeAsAdmin({ commit, dispatch }) {
    commit('setIsAdminAppointmentTypeLoading', true)

    try {
      const { data } = await api.get('/admin/appointment/serviceTypes')
      commit('setAppointmentTypes', data)
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsAdminAppointmentTypeLoading', false)
    }
  },

  async fetchAppointmentQuestionnairesAsAdmin({ commit, dispatch }, id) {
    commit('setAppointmentQuestionnaires', {})

    try {
      const { data } = await api.get(`/admin/questionnaire/summary/${id}`)

      commit('setAppointmentQuestionnaires', data)
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    }
  },

  async rescheduleAppointmentAsAdmin({ commit, dispatch }, rescheduleData) {
    if (!rescheduleData?.id) {
      dispatch('showErrorMessage', Vue.i18n.translate('appointment.messages.appointmentNotFound'))
      return
    }

    await api
      .put(`${endpoint()}/appointment/reschedule/${rescheduleData.id}`, rescheduleData)
      .then(() => {
        commit('closeAllModals')
        dispatch('fetchAppointmentAsAdmin', rescheduleData.id)
        dispatch('showSuccessMessage', Vue.i18n.translate('appointment.messages.rescheduled'))
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', `${data.errors[0].field}: ${data.errors[0].message}`)
          : dispatch('showErrorMessage', data.message)
      })
  },

  async updateAppointmentDisplacementAsAdmin({ commit, dispatch }, { id, displacement }) {
    if (!id) {
      dispatch('showErrorMessage', Vue.i18n.translate('appointment.messages.appointmentNotFound'))
      return
    }

    await api
      .put(`${endpoint()}/appointment/${id}/displacement`, { displacement })
      .then(() => {
        commit('closeAllModals')
        dispatch('fetchAppointmentAsAdmin', id)
        dispatch('showSuccessMessage', Vue.i18n.translate('appointment.adminActions.updateDisplacement.successMessage'))
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', `${data.errors[0].field}: ${data.errors[0].message}`)
          : dispatch('showErrorMessage', data.message)
      })
  },

  async updateAppointmentObservationsAsAdmin({ commit, dispatch }, requestBody) {
    await api
      .post('/admin/appointment/updateObservations', requestBody)
      .then(() => {
        commit('closeAllModals')
        dispatch('fetchAppointmentAsAdmin', requestBody.appointment_id)
        dispatch('showSuccessMessage', Vue.i18n.translate('appointment.adminActions.updateObservations.successMessage'))
      })
      .catch(({ data }) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', `${data.errors[0].field}: ${data.errors[0].message}`)
          : dispatch('showErrorMessage', data.message)
      })
  },

  async appointmentChangeHost({ commit, dispatch }, body) {
    await api
      .post(`/admin/appointment/changeHost`, body)
      .then(() => {
        commit('closeAllModals')
        dispatch('fetchAppointmentAsAdmin', body.appointment_id)
      })
      .catch(({ data }) =>
        dispatch(
          'showErrorMessage',
          data ? data.message : 'Erro ao atualizar host'
        )
      )
  },

  async appointmentAddHost({ commit, dispatch }, body) {
    await api
      .post(`/admin/appointment/setHost`, body)
      .then(() => {
        commit('closeAllModals')
        dispatch('showSuccessMessage', Vue.i18n.translate('appointment.adminActions.addHost.successMessage'))
        dispatch('fetchAppointmentAsAdmin', body.appointment_id)
      })
      .catch(({ data }) =>
        dispatch(
          'showErrorMessage',
          data ? data.message : Vue.i18n.translate('appointment.adminActions.addHost.errorMessage')
        )
      )
  },

  async changeAppointmentType({ commit, dispatch }, body) {
    await api
      .post(`/admin/appointment/changeType`, body)
      .then(() => {
        commit('closeAllModals')
        dispatch('fetchAppointmentAsAdmin', body.appointment_id)
      })
      .catch(({ data }) =>
        dispatch(
          'showErrorMessage',
          data ? data.message : 'Erro ao atualizar tipo de serviço'
        )
      )
  },

  async changeAppointmentKeyLocation({ commit, dispatch }, body) {
    await api
      .post(`/admin/appointment/changeKeyLocation`, body)
      .then(() => {
        commit('closeAllModals')
        dispatch('fetchAppointmentAsAdmin', body.appointment_id)
      })
      .catch(({ data }) =>
        dispatch(
          'showErrorMessage',
          data ? data.message : Vue.i18n.translate('appointment.adminActions.changeKeyLocation.errorMessage')
        )
        )
  },

  async generateAdminAppointmentExcel({ rootState }) {
      const defaultParams = {
        size: rootState.paginate.itemsPerPage,
        page: rootState.paginate.currentPage - 1,
      }
      const params = { ...defaultParams, ...rootState.filters.filters }

    return api.get('/admin/appointment/createReport', {
     params,
     paramsSerializer: (params) =>
       qs.stringify(params, { arrayFormat: 'repeat' }),
      responseType: 'arraybuffer'
    })
  },

  async reopenAppointmentQuote({ commit, dispatch }, body) {
    await api
      .post(`/admin/appointment/reopenQuote`, body)
      .then(() => {
        commit('closeAllModals')
        dispatch('fetchAppointmentAsAdmin', body.appointment_id)
        dispatch('fetchServiceReportAsAdmin', body.appointment_id)
        dispatch('showSuccessMessage', Vue.i18n.translate('appointment.adminActions.quoteReopen.successMessage'))
      })
      .catch(({ data }) =>
        dispatch(
          'showErrorMessage',
          data ? data.message : Vue.i18n.translate('appointment.adminActions.quoteReopen.errorMessage')
        )
      )
  },

  async changeAppointmentDate({ commit, dispatch }, body) {
    await api
      .post(`/admin/appointment/changeDate`, body)
      .then(() => {
        commit('closeAllModals')
        dispatch('fetchAppointmentAsAdmin', body.appointment_id)
        dispatch('fetchServiceReportAsAdmin', body.appointment_id)
        dispatch('showSuccessMessage', Vue.i18n.translate('appointment.adminActions.changeDate.successMessage'))
      })
      .catch(({ data }) =>
        dispatch(
          'showErrorMessage',
          data ? data.message : Vue.i18n.translate('appointment.adminActions.changeDate.errorMessage')
        )
      )
  },

  // GROUP STUFF --------------------------------------------------
  async partnerCreateAppointmentGroup({ commit, dispatch }, body) {
    commit('setIsLoading', true)

    try {
      await api.post(`${endpoint()}/appointmentGroup/create`, body)
      dispatch('showSuccessMessage', Vue.i18n.translate('appointment.messages.groupCreated'))
    } catch({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async removeGroup({ commit, dispatch }, groupId) {
    commit('setIsLoading', true)

    try {
      await api.post(`${endpoint()}/appointmentGroup/undo/${groupId}`)
      dispatch('showSuccessMessage', 'Group removed with success')
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async addAppointmentToGroup({ commit, dispatch }, { group_id, appointments }) {
    try {
      await api.post(`${endpoint()}/appointmentGroup/addAppointment`, { group_id, appointments })
      dispatch('showSuccessMessage', 'Appointment added to group with success')
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    }
  },

  async partnerRemoveAppointmentFromGroup({ commit, dispatch }, body) {
    commit('setIsLoading', true)

    try {
      await api.post(`${endpoint()}/appointmentGroup/removeAppointment`, body)
      dispatch('showSuccessMessage', 'Appointment removed from group with success')
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async setGroupHost({ commit, dispatch }, body) {
    try {
      await api.post(`${endpoint()}/appointmentGroup/setHost`, body)
      dispatch('showSuccessMessage', 'Host set with success')
      commit('closeAllModals')
    } catch({ data }) {
      dispatch('showErrorMessage', data.message)
    }
  },

  async unassignGroupHost({ commit, dispatch }, groupId) {
    try {
      await api.post(`${endpoint()}/appointmentGroup/unassignHost`, groupId)
      dispatch('showSuccessMessage', 'Host unassign with success')
      commit('closeAllModals')
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    }
  },
}

const mutations = {
  setIsAdminAppointmentTypeLoading: (state, status) => {
    state.isAdminAppointmentTypeLoading = status
  },

  setIsAppointmentRescheduling: (state, status) => {
    state.isAppointmentRescheduling = status
  }
}

export default {
  state,
  mutations,
  actions,
  getters,
}
